import { notifications } from '@mantine/notifications'
import * as Sentry from '@sentry/react'
import merge from 'deepmerge'
import { reaction } from 'mobx'
import type { Instance, SnapshotOut } from 'mobx-state-tree'
import { types } from 'mobx-state-tree'

import type {
  CheckUserResponse,
  LoginResponse,
  LoginResponseError,
  SafeRequestResponse,
  SetupProgress,
  UpdateSponsorSetupProgressParams,
} from '@/services/api/api.types'
import { getDefaultSetupProgress, isSetupProgressEmpty } from '@/services/api/api.types'
import {
  getGrandfatheredProgress,
  type GrandfatheringData,
  shouldGrandfatherSponsor,
} from '@/services/setupProgressService'
import { withRootStore } from '@/services/state/helpers/withRootStore'
import { withSetProp } from '@/services/state/helpers/withSetProp'
import { getDefaultSponsor } from '@/utils/sponsors'
import * as storage from '@/utils/storage'

export const NO_CHANNELS_MESSAGE =
  'This account does not currently have access to any channels on the Sidekick Platform.'

export const ACCOUNT_NOT_SET_UP_MESSAGE =
  'This account is not fully set up yet. Contact support to finish setting it up.'

export const SIGN_IN_INCOMPLETE_MESSAGE =
  'We could not finish signing you in. Try again, and contact support if this keeps happening.'

// NO_CHANNEL_MEMBERSHIP and INTERNAL_ERROR share one message text, so only the code tells an
// unfinished account from a failed lookup (C2-2700).
const FRIENDLY_LOGIN_ERROR_CODES: Partial<Record<LoginResponseError['code'], string>> = {
  NO_CHANNEL_MEMBERSHIP: ACCOUNT_NOT_SET_UP_MESSAGE,
  INTERNAL_ERROR: SIGN_IN_INCOMPLETE_MESSAGE,
}

// The no-channels case still has no code. The sponsor_id text covers a backend without C2-2700,
// where it can mean either case, hence the retry copy.
const FRIENDLY_LOGIN_MESSAGES: Record<string, string> = {
  'environment configuration not found': NO_CHANNELS_MESSAGE,
  'user configuration error - sponsor_id not found': SIGN_IN_INCOMPLETE_MESSAGE,
}

function friendlyLoginError(
  code: LoginResponseError['code'] | undefined,
  message: string | undefined,
): string | undefined {
  return (
    (code && FRIENDLY_LOGIN_ERROR_CODES[code]) ||
    (message && FRIENDLY_LOGIN_MESSAGES[message.trim().toLowerCase()]) ||
    undefined
  )
}

type BackendErrorBody = { message?: unknown; error?: unknown; detail?: unknown }

// Error text lands under `error` (sme_server's middleware rewrites HTTPException), `detail`
// (FastAPI's default handler), or `message`, depending on which layer rejected the request.
// `detail` is sometimes an object, so only strings are accepted.
function getBackendErrorText(data: BackendErrorBody): string | undefined {
  const text = data.error ?? data.detail ?? data.message
  return typeof text === 'string' ? text : undefined
}

// A disabled account is rejected with the same 401 as an end user, but must not be told to
// use the app. Substring match: the backend wording is free text and varies by guard.
function isDisabledAccountError(detail: string | undefined): boolean {
  return !!detail?.toLowerCase().includes('disabled')
}

// check_user returns no usable body for end users (it 401s), so read the id from the
// token we just received. Telemetry only - never used for an access decision.
function getUserIdFromIdToken(idToken: string | undefined): string | undefined {
  const payload = idToken?.split('.')[1]
  if (!payload) return undefined

  try {
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      sub?: string
      'cognito:username'?: string
    }
    return claims['cognito:username'] ?? claims.sub
  } catch {
    return undefined
  }
}

export const AuthenticationStoreModel = types
  .model('AuthenticationStore')
  .props({
    ready: types.optional(types.boolean, false),
    auth: types.maybe(types.frozen<LoginResponse['authenticationResult']>()),
    user: types.maybe(types.frozen<CheckUserResponse>()),
    setupProgress: types.optional(types.frozen<SetupProgress>(), getDefaultSetupProgress()),
    isInSetup: types.optional(types.boolean, false),
    setupStatusChecked: types.optional(types.boolean, false),
    pendingSponsorSelection: types.optional(types.boolean, false),
    availableSponsors: types.optional(types.frozen<LoginResponse['availableSponsors']>(), []),
  })
  .extend(withSetProp)
  .extend(withRootStore)
  .volatile<AuthenticationStoreVolatileProps>(() => ({
    refreshTokenTimer: undefined,
    tokenExpiresAt: undefined,
    _refreshPromise: null,
    _cleanupTokenFreshnessListeners: undefined,
  }))
  .views(self => ({
    get isAuthenticated() {
      return !!self.auth?.idToken
    },
    get isSponsorAdmin() {
      return (self.user?.isSponsorAdmin || self.user?.isSponsor) ?? false
    },
    get isSwiAdmin() {
      return self.user?.isSwiAdmin ?? false
    },
  }))
  .actions(self => ({
    reset() {
      Sentry.setUser(null)
      self.auth = undefined
      self.user = undefined
      self.setupProgress = getDefaultSetupProgress()
      self.isInSetup = false
      self.setupStatusChecked = false
      self.pendingSponsorSelection = false
      self.availableSponsors = []
      self.rootStore.api.queryClient.clear()
      self.rootStore.api.setAuthToken(null)
      self.rootStore.configStore.setProp('serverBaseUrl', undefined)
      self.rootStore.configStore.setProp('analyticsServerBaseUrl', undefined)
      self.rootStore.api.setBaseUrl(undefined)
      storage.clearExceptPersistent()
      clearTimeout(self.refreshTokenTimer)
      self.refreshTokenTimer = undefined
      self.tokenExpiresAt = undefined
      self._refreshPromise = null
      self._cleanupTokenFreshnessListeners?.()
    },
    /**
     * Sets the server URLs for API calls and configuration
     * @param serverUrl - The SME server URL
     * @param analyticsUrl - The analytics server URL (optional, defaults to serverUrl)
     */
    setServerUrls(serverUrl: string, analyticsUrl?: string) {
      self.rootStore.api.setBaseUrl(serverUrl)
      self.rootStore.configStore.setProp('serverBaseUrl', serverUrl)
      self.rootStore.configStore.setProp('analyticsServerBaseUrl', analyticsUrl || serverUrl)
    },
  }))
  .actions(self => ({
    async checkSetupStatus() {
      // Only check setup status for sponsor admins
      if (!self.isSponsorAdmin) {
        self.setProp('isInSetup', false)
        self.setProp('setupStatusChecked', true)
        return
      }

      // Check if setup is complete based on progress
      const progress = self.setupProgress
      const allStepsComplete = progress.createContent && progress.inviteUsers

      self.setProp('isInSetup', !allStepsComplete)

      self.setProp('setupStatusChecked', true)
    },
    async fetchSetupProgress() {
      if (!self.isSponsorAdmin) {
        self.setProp('setupProgress', getDefaultSetupProgress())
        return
      }

      try {
        const setupProgress = await this.getSetupProgress()
        self.setProp('setupProgress', setupProgress)
      } catch (error) {
        console.error('Failed to fetch setup progress:', error)
        self.setProp('setupProgress', getDefaultSetupProgress())
      }
    },
    async getSetupProgress(): Promise<SetupProgress> {
      if (!self.isSponsorAdmin) {
        return getDefaultSetupProgress()
      }

      try {
        const sponsorInfo = await self.rootStore.api.getSponsorInfo()
        const currentProgress = sponsorInfo?.setupProgress ?? getDefaultSetupProgress()

        // If we have existing setup progress with completed steps, return it
        if (!isSetupProgressEmpty(currentProgress)) {
          return currentProgress
        }

        // Check if this sponsor should be grandfathered in
        // (existing sponsor with no setup progress but has sponsor-specific content)
        const contentModules = await self.rootStore.api.getContentModules()
        const grandfatheringData: GrandfatheringData = { contentModules }

        if (shouldGrandfatherSponsor(grandfatheringData, currentProgress)) {
          const grandfatheredProgress = getGrandfatheredProgress()

          // Save the grandfathered progress to the backend
          try {
            if (self.user?.sponsorId) {
              const params: UpdateSponsorSetupProgressParams = {
                id: self.user.sponsorId,
                setup_progress: grandfatheredProgress,
              }
              await self.rootStore.api.updateSponsorSetupProgress(params)
            }
            return grandfatheredProgress
          } catch (saveError) {
            console.error('Failed to save grandfathered setup progress:', saveError)
            // Even if save fails, return the grandfathered progress for this session
            return grandfatheredProgress
          }
        }

        // New sponsor with no content - return default progress
        return getDefaultSetupProgress()
      } catch (error) {
        console.error('Failed to fetch setup progress:', error)
        return getDefaultSetupProgress()
      }
    },
    async logIn(params: { email: string; password: string }): Promise<{
      ok: boolean
      message: string
      code?: LoginResponseError['code']
      endUser?: { userId?: string }
    }> {
      const response = await self.rootStore.api.safeRequest<typeof self.rootStore.api.login, LoginResponseError>(
        self.rootStore.api.login,
        {
          username: params.email,
          password: params.password,
        },
      )

      return this.processAuthResponse(response, true)
    },
    async refresh() {
      if (!self.auth?.refreshToken) {
        void self.rootStore.reset()
        self.setProp('ready', true)
        return
      }

      const response = await self.rootStore.api.safeRequest<typeof self.rootStore.api.refreshToken, LoginResponseError>(
        self.rootStore.api.refreshToken,
        { refresh_token: self.auth.refreshToken },
      )

      const processedResponse = await this.processAuthResponse(response, false)

      if (!processedResponse.ok) {
        void self.rootStore.reset()
      }

      // Mark as ready - setup status is checked during processAuthResponse
      self.setProp('ready', true)
    },
    async logOut() {
      await self.rootStore.api.safeRequest(self.rootStore.api.logout, {
        access_token: self.auth?.accessToken!,
        refresh_token: self.auth?.refreshToken!,
      })
      await self.rootStore.reset()
    },
    startRefreshTokenTimer() {
      if (!self.auth?.expiresIn) return

      clearTimeout(self.refreshTokenTimer)
      self.tokenExpiresAt = Date.now() + self.auth.expiresIn * 1000
      const REFRESH_AT_FRACTION = 0.9
      const refreshMs = self.auth.expiresIn * REFRESH_AT_FRACTION * 1000
      self.refreshTokenTimer = setTimeout(() => void this.refreshTokenSilently(), refreshMs)
    },
    /**
     * Lightweight token refresh that only swaps the token without heavy side effects
     * (no checkUser, fetchSetupProgress, notifyLogin, etc.).
     * Built-in shared-promise deduplication ensures concurrent callers (interceptor,
     * visibility/focus listener, timer) coalesce into a single in-flight refresh.
     * Returns the new idToken on success, null on failure.
     */
    _clearRefreshPromise() {
      self._refreshPromise = null
    },
    refreshTokenSilently(): Promise<string | null> {
      if (self._refreshPromise) return self._refreshPromise

      self._refreshPromise = this._doTokenRefresh().finally(() => {
        this._clearRefreshPromise()
      })
      return self._refreshPromise
    },
    /**
     * Calls the refresh endpoint directly (bypassing safeRequest) so we can
     * distinguish auth failures (401/403 -> session reset) from transient
     * network errors (-> silent null, let the next user action retry).
     */
    async _doTokenRefresh(): Promise<string | null> {
      if (!self.auth?.refreshToken) return null

      try {
        const response = await self.rootStore.api.refreshToken({
          refresh_token: self.auth.refreshToken,
        })

        if (!response.authenticationResult?.idToken) return null

        // Deep merge to preserve refreshToken (not included in refresh responses)
        self.setProp('auth', merge(self.auth ?? {}, response.authenticationResult))
        self.rootStore.api.setAuthToken(response.authenticationResult.idToken)

        this.startRefreshTokenTimer()

        return response.authenticationResult.idToken
      } catch (error: any) {
        const status = error?.response?.status
        if (status === 401 || status === 403) {
          notifications.show({
            title: 'Session expired',
            message: 'Your session has expired. Please log in again.',
            color: 'orange',
            position: 'bottom-left',
          })
          void self.rootStore.reset()
        } else {
          console.warn('Silent token refresh failed (network/timeout):', error)
        }
        return null
      }
    },
    async processAuthResponse(
      response: SafeRequestResponse<LoginResponse, LoginResponseError>,
      isFreshLogin: boolean = false,
    ): Promise<{
      ok: boolean
      message: string
      code?: LoginResponseError['code']
      endUser?: { userId?: string }
    }> {
      if (response.ok) {
        if (response.data.authenticationResult?.idToken) {
          self.rootStore.api.setAuthToken(response.data.authenticationResult.idToken)

          // Store available sponsors if provided (for account menu display)
          if (response.data.availableSponsors && response.data.availableSponsors.length > 0) {
            self.setProp('availableSponsors', response.data.availableSponsors)
          }

          // Check if sponsor selection is required
          if (response.data.requiresSponsorSelection && response.data.availableSponsors) {
            if (response.data.availableSponsors.length === 0) {
              return { ok: false, message: NO_CHANNELS_MESSAGE }
            }

            // Set sponsor selection state (MobX batches these synchronous updates)
            self.setProp('pendingSponsorSelection', true)
            self.setProp('auth', response.data.authenticationResult)

            // Use the default sponsor's environment for all API calls
            // This ensures we're calling the correct environment for the current user
            const defaultSponsor = getDefaultSponsor(response.data.availableSponsors)

            if (!defaultSponsor) {
              return {
                ok: false,
                message: 'No default channel available.',
              }
            }

            // Set the server URLs to the default sponsor's environment
            const serverUrl = defaultSponsor.targetUrls.smeServerUrl
            const analyticsUrl = defaultSponsor.targetUrls.smeServerUrl
            self.setServerUrls(serverUrl, analyticsUrl)

            // Reset sponsor context even when sponsor selection is required
            if (isFreshLogin) {
              try {
                const resetResponse = await self.rootStore.api.resetSponsorContext()
                console.log('Sponsor context reset (sponsor selection required):', resetResponse)

                // Small delay to allow browser to save the sponsor cookie (see C2-1801)
                // This prevents race conditions where subsequent API calls don't receive the cookie
                await new Promise(resolve => setTimeout(resolve, 100))
              } catch (error) {
                return {
                  ok: false,
                  message: `Failed to initialize sponsor context: ${error instanceof Error ? error.message : 'Unknown error'}`,
                }
              }
            }

            console.log('Sponsor selection state set:', {
              availableSponsors: self.availableSponsors?.length,
              pendingSponsorSelection: self.pendingSponsorSelection,
              isAuthenticated: self.isAuthenticated,
            })

            // Don't continue with normal flow - wait for sponsor selection
            return { ok: true, message: 'Sponsor selection required' }
          }

          // Set server URLs with environment variable override priority
          const envServerUrl = import.meta.env.VITE_SERVER_BASE_URL
          const envAnalyticsUrl = import.meta.env.VITE_ANALYTICS_SERVER_BASE_URL
          const serverUrl = envServerUrl || response.data.targetUrls?.smeServerUrl
          const analyticsUrl = envAnalyticsUrl || response.data.targetUrls?.smeServerUrl

          // Set server URLs - use new ones if available, otherwise use existing ones
          if (serverUrl) {
            self.setServerUrls(serverUrl, analyticsUrl)
          } else if (self.rootStore.configStore.serverBaseUrl) {
            // Use existing server URL for API calls during refresh
            self.rootStore.api.setBaseUrl(self.rootStore.configStore.serverBaseUrl)
          } else {
            // Only fail if we don't have server URLs at all
            return {
              ok: false,
              message: 'No server URL available. Please check your configuration.',
            }
          }

          // Reset sponsor context on the environment server with the login token
          // This must complete successfully before proceeding to other API calls
          // Only call reset during fresh login, not during token refresh
          if (isFreshLogin) {
            try {
              const resetResponse = await self.rootStore.api.resetSponsorContext()
              console.log('Sponsor context reset:', resetResponse)

              // Small delay to allow browser to save the sponsor cookie (see C2-1801)
              // This prevents race conditions where subsequent API calls don't receive the cookie
              await new Promise(resolve => setTimeout(resolve, 100))
            } catch (error) {
              // This is a critical step - if it fails, we should not proceed
              return {
                ok: false,
                message: `Failed to initialize sponsor context: ${error instanceof Error ? error.message : 'Unknown error'}`,
              }
            }
          }

          const userResponse = await self.rootStore.api.safeRequest<
            typeof self.rootStore.api.checkUser,
            BackendErrorBody
          >(self.rootStore.api.checkUser)

          if (userResponse.ok) {
            Sentry.setUser({
              id: userResponse.data.userId,
              ...userResponse.data,
            })
          }

          const failureDetail = userResponse.ok ? undefined : getBackendErrorText(userResponse.data)

          // End users are rejected with a 401 before any role flag is read: for service_type
          // "sme" the backend filters their sponsor out of the token claims, so it finds no
          // user auth record. stag/intrl instead answer 200 with access.sme false. A disabled
          // account also 401s here and must keep the error path instead of being sent to the app.
          const isEndUser = userResponse.ok
            ? !userResponse.data.access.sme
            : userResponse.statusCode === 401 && !isDisabledAccountError(failureDetail)

          if (isEndUser) {
            // Discard everything the flow set up before the role was known: bearer token,
            // available sponsors, and the server URLs pointed at this user's environment.
            const userId = getUserIdFromIdToken(response.data.authenticationResult.idToken)
            self.reset()

            return {
              ok: false,
              message: '',
              endUser: { userId },
            }
          }

          if (!userResponse.ok) {
            return {
              ok: false,
              message: failureDetail || 'You are not authorized to use this application.',
            }
          } else {
            self.setProp('user', userResponse.data)

            // Fetch setup progress and check setup status after user is set
            await this.fetchSetupProgress()
            await this.checkSetupStatus()

            // Notify server of successful login/authentication event
            try {
              await self.rootStore.api.notifyLogin({})
            } catch (error) {
              // Non-blocking: log error but don't prevent authentication completion
              console.warn('Failed to notify login event:', error)
            }

            // Mark as ready so routing can proceed
            self.setProp('ready', true)
          }

          self.setProp('auth', merge(self.auth ?? {}, response.data.authenticationResult)) // merging because refresh token response does not include new refresh token

          this.startRefreshTokenTimer()

          return { ok: true, message: 'Success' }
        } else {
          return { ok: false, message: 'Something went wrong.' }
        }
      } else {
        // NO_PORTAL_ACCESS is answered before any token exists, so the id comes from the body
        // rather than an id token (C2-2634).
        if (response.data.code === 'NO_PORTAL_ACCESS') {
          return { ok: false, message: '', endUser: { userId: response.data.user_id ?? undefined } }
        }

        const backendMessage = response.data.message ?? response.error?.message
        // The copy is written for sign-in, so a refresh failure keeps the backend's own text.
        const friendlyMessage = isFreshLogin ? friendlyLoginError(response.data.code, backendMessage) : undefined
        return {
          ok: false,
          message: friendlyMessage ?? backendMessage ?? 'Something went wrong.',
          code: response.data.code,
        }
      }
    },
    async selectSponsor(
      sponsor: NonNullable<LoginResponse['availableSponsors']>[0],
    ): Promise<{ ok: boolean; message: string; error?: any; statusCode?: number }> {
      try {
        // Save original sponsor URLs to restore on failure
        const originalServerUrl = self.rootStore.configStore.serverBaseUrl
        const originalAnalyticsUrl = self.rootStore.configStore.analyticsServerBaseUrl

        // Set server URLs to the selected sponsor's environment
        const serverUrl = sponsor.targetUrls.smeServerUrl
        const analyticsUrl = sponsor.targetUrls.smeServerUrl

        // Block all concurrent httpClient requests while the switch is in flight.
        // Without this, React Query refetches or polling can hit the new domain
        // before the sponsor cookie is set, causing the backend to issue a
        // default-sponsor cookie that overwrites the correct one.
        let switchResponse
        self.rootStore.api.beginSponsorSwitch()
        try {
          if (serverUrl) {
            self.setServerUrls(serverUrl, analyticsUrl)
          }

          switchResponse = await self.rootStore.api.switchSponsor(sponsor.sponsorId)
          console.log('Sponsor switch response:', switchResponse)

          // Small delay to allow browser to save the sponsor cookie (see C2-1801)
          await new Promise(resolve => setTimeout(resolve, 100))
        } catch (error) {
          // Restore original sponsor URLs on failure
          if (originalServerUrl) {
            self.setServerUrls(originalServerUrl, originalAnalyticsUrl)
          }

          // Extract error details for debugging
          const e = error as any
          const statusCode = e.response?.status
          const serverError = e.response?.data

          return {
            ok: false,
            message: `Failed to switch sponsor context: ${error instanceof Error ? error.message : 'Unknown error'}`,
            error: serverError,
            statusCode,
          }
        } finally {
          self.rootStore.api.endSponsorSwitch()
        }

        // Ensure the switch completed successfully before proceeding
        if (!switchResponse) {
          // Restore original sponsor URLs on failure
          if (originalServerUrl) {
            self.setServerUrls(originalServerUrl, originalAnalyticsUrl)
          }

          return {
            ok: false,
            message: 'Sponsor switch did not return a valid response',
          }
        }

        // Clear all cached queries (content modules, metrics, etc.) to prevent showing stale data
        self.rootStore.api.queryClient.clear()

        // Continue with normal authentication flow
        const userResponse = await self.rootStore.api.safeRequest(self.rootStore.api.checkUser)

        if (userResponse.ok) {
          Sentry.setUser({
            id: userResponse.data.userId,
            ...userResponse.data,
          })
        }

        if (!userResponse.ok || !userResponse.data.access.sme) {
          return {
            ok: false,
            message: userResponse.data?.message || 'You are not authorized to use this application.',
          }
        } else {
          // Reset setup state before setting new user to prevent stale isInSetup
          // from briefly showing the "Before you Start" popup for the new sponsor
          self.setProp('isInSetup', false)
          self.setProp('setupProgress', getDefaultSetupProgress())
          self.setProp('setupStatusChecked', false)

          self.setProp('user', userResponse.data)

          // Broadcast so other tabs can show "channel changed, please reload" modal
          storage.broadcastChannelSwitch(userResponse.data.sponsorId)

          // Fetch setup progress and check setup status after user is set
          // These make remote calls which can be unreliable, so we handle failures gracefully
          try {
            await this.fetchSetupProgress()
            await this.checkSetupStatus()
          } catch (error) {
            console.error('Failed to fetch setup information:', error)
            // Continue with login even if setup info fails - use defaults
            // fetchSetupProgress already has fallback logic for defaults
          }

          // Clear sponsor selection state only after successful completion
          self.setProp('pendingSponsorSelection', false)

          // Notify server of successful sponsor switch event
          try {
            await self.rootStore.api.notifyLogin({})
          } catch (error) {
            // Non-blocking: log error but don't prevent sponsor switch completion
            console.warn('Failed to notify sponsor switch event:', error)
          }

          // Mark as ready so routing can proceed
          self.setProp('ready', true)
        }

        this.startRefreshTokenTimer()

        return { ok: true, message: 'Success' }
      } catch (error) {
        return {
          ok: false,
          message: `Error selecting sponsor: ${error instanceof Error ? error.message : 'Unknown error'}`,
        }
      }
    },
  }))
  .actions(self => ({
    async updateSetupProgress(progress: Partial<SetupProgress>): Promise<{ ok: boolean; message: string }> {
      if (!self.user?.sponsorId) {
        return { ok: false, message: 'User not found or missing sponsor ID' }
      }

      const currentProgress: SetupProgress = self.setupProgress
      const updatedProgress: SetupProgress = { ...currentProgress, ...progress }

      const params = { setup_progress: updatedProgress }

      try {
        await self.rootStore.api.updateSponsorSetupProgress(params)

        // Update the local setup progress
        self.setProp('setupProgress', updatedProgress)

        // Check if setup is now complete and update isInSetup status
        self.checkSetupStatus()

        return { ok: true, message: 'Setup progress updated successfully' }
      } catch (error) {
        return {
          ok: false,
          message: `Network error while updating setup progress: ${error instanceof Error ? error.message : 'Unknown error'}`,
        }
      }
    },

    // Developer functions for resetting setup progress
    // ------------------------------------------------------------
    async resetSetupProgress(): Promise<{ ok: boolean; message: string }> {
      if (!self.user?.sponsorId) {
        return { ok: false, message: 'User not found or missing sponsor ID' }
      }

      try {
        const emptyProgress = {} as SetupProgress
        const params = { setup_progress: emptyProgress }
        await self.rootStore.api.updateSponsorSetupProgress(params)

        // Update local state directly without triggering grandfathering check
        self.setProp('setupProgress', emptyProgress)
        self.setProp('isInSetup', true)

        return { ok: true, message: 'Setup progress reset successfully' }
      } catch (error) {
        return {
          ok: false,
          message: `Error resetting setup progress: ${error instanceof Error ? error.message : 'Unknown error'}`,
        }
      }
    },
  }))
  .actions(self => ({
    afterAttach() {
      reaction(
        () => self.rootStore.configStore.ready,
        ready => ready && void self.refresh(),
      )

      self.rootStore.api.setTokenRefresher(() => self.refreshTokenSilently())

      const EXPIRY_BUFFER_MS = 60_000
      const onTokenFreshnessCheck = () => {
        if (!self.isAuthenticated || !self.tokenExpiresAt) return
        if (Date.now() >= self.tokenExpiresAt - EXPIRY_BUFFER_MS) {
          void self.refreshTokenSilently()
        }
      }

      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') onTokenFreshnessCheck()
      }

      document.addEventListener('visibilitychange', onVisibilityChange)
      window.addEventListener('focus', onTokenFreshnessCheck)

      self._cleanupTokenFreshnessListeners = () => {
        document.removeEventListener('visibilitychange', onVisibilityChange)
        window.removeEventListener('focus', onTokenFreshnessCheck)
        self._cleanupTokenFreshnessListeners = undefined
      }
    },
    beforeDestroy() {
      clearTimeout(self.refreshTokenTimer)
      self.refreshTokenTimer = undefined
      self._cleanupTokenFreshnessListeners?.()
    },
  }))

export interface AuthenticationStoreVolatileProps {
  refreshTokenTimer: NodeJS.Timeout | undefined
  tokenExpiresAt: number | undefined
  _refreshPromise: Promise<string | null> | null
  _cleanupTokenFreshnessListeners: (() => void) | undefined
}
export interface AuthenticationStore extends Instance<typeof AuthenticationStoreModel> {}
export interface AuthenticationStoreSnapshot extends SnapshotOut<typeof AuthenticationStoreModel> {}
